"use client";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMemo } from "react";
import SalifyLink from "src/lib/components/link/salify-link";
import { toAssetImages } from "src/lib/utils";
import {
  LayoutViewMode,
  ResponsivePropsConfig,
  resolveResponsiveProps,
  splitDesktopAndResponsive,
} from "src/lib/utils/responsive-props";

export type CategoryLink = {
  text: string;
  url: string;
};

export type ProductCategoryItem = {
  thumbnail?: string;
  title: string;
  description?: string;
  link?: CategoryLink;
};

export type CategoryButton = {
  text: string;
  link: string;
};

type CategoryViewportData = {
  backgroundImage?: string;
  heading1?: string;
  heading2?: string;
  categories?: ProductCategoryItem[];
  button?: CategoryButton;
};

export interface VariantProps extends CategoryViewportData {
  responsive?: ResponsivePropsConfig<CategoryViewportData>;
}

const useLayoutViewMode = (): LayoutViewMode => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("tablet"));
  const isTablet = useMediaQuery(theme.breakpoints.between("tablet", "laptop"));
  if (isMobile) return "mobile";
  if (isTablet) return "tablet";
  return "desktop";
};

const CategoryCard = ({ item }: { item: ProductCategoryItem }) => {
  const thumbUrl = item.thumbnail?.trim() ? toAssetImages(item.thumbnail as string) : "";

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {thumbUrl ? (
        <Box
          component="img"
          src={thumbUrl}
          alt={item.title}
          loading="lazy"
          sx={{
            width: "100%",
            height: { xs: 160, laptop: 200 },
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: { xs: 160, laptop: 200 },
            bgcolor: "grey.200",
          }}
        />
      )}
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          p: { xs: 2, laptop: 2.5 },
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontWeight: 700,
            fontSize: { xs: 16, laptop: 18 },
            lineHeight: 1.3,
            color: "text.primary",
          }}
        >
          {item.title}
        </Typography>
        {item.description ? (
          <Typography
            sx={{
              flex: 1,
              fontSize: { xs: 13, laptop: 14 },
              lineHeight: 1.65,
              color: "text.secondary",
            }}
          >
            {item.description}
          </Typography>
        ) : null}
        {item.link?.text ? (
          <SalifyLink href={item.link.url || "#"}>
            <Typography
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: 14,
                fontWeight: 600,
                color: "primary.main",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {item.link.text}
              <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </Typography>
          </SalifyLink>
        ) : null}
      </CardContent>
    </Card>
  );
};

const FixedProductCategoryDefaultV2 = (props: VariantProps) => {
  const viewMode = useLayoutViewMode();
  const { desktop, responsive } = splitDesktopAndResponsive(
    props as unknown as Record<string, unknown>,
  );

  const data = useMemo((): CategoryViewportData => {
    const resolved = resolveResponsiveProps(
      {
        backgroundImage: (desktop.backgroundImage as string) || "",
        heading1: (desktop.heading1 as string) || "",
        heading2: (desktop.heading2 as string) || "",
        categories: (desktop.categories as ProductCategoryItem[]) || [],
        button: desktop.button as CategoryButton | undefined,
      },
      responsive,
      viewMode,
      ["backgroundImage", "heading1", "heading2", "categories", "button"],
    );

    return {
      backgroundImage: String(resolved.backgroundImage || ""),
      heading1: String(resolved.heading1 || ""),
      heading2: String(resolved.heading2 || ""),
      categories: Array.isArray(resolved.categories) ? resolved.categories : [],
      button: resolved.button as CategoryButton | undefined,
    };
  }, [desktop, responsive, viewMode]);

  const backgroundUrl = data.backgroundImage?.trim()
    ? toAssetImages(data.backgroundImage as string)
    : "";
  const categories = data.categories?.filter((item) => item?.title?.trim()) || [];

  return (
    <Box
      component="section"
      id="fixed-product-category"
      sx={{
        position: "relative",
        py: { xs: 6, laptop: 8 },
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        bgcolor: backgroundUrl ? undefined : "grey.900",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: backgroundUrl ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.7)",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: { xs: 4, laptop: 5 } }}>
          {data.heading1 ? (
            <Typography
              component="h2"
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: { xs: 24, tablet: 28, laptop: 32 },
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                mb: data.heading2 ? 1.5 : 0,
              }}
            >
              {data.heading1}
            </Typography>
          ) : null}
          {data.heading2 ? (
            <Typography
              sx={{
                color: "rgba(255,255,255,0.88)",
                fontSize: { xs: 14, laptop: 16 },
                maxWidth: 720,
                mx: "auto",
                lineHeight: 1.6,
              }}
            >
              {data.heading2}
            </Typography>
          ) : null}
        </Box>

        {categories.length > 0 ? (
          <Grid container spacing={{ xs: 2, laptop: 3 }}>
            {categories.map((item, index) => (
              <Grid item xs={12} tablet={6} laptop={4} key={`${item.title}-${index}`}>
                <CategoryCard item={item} />
              </Grid>
            ))}
          </Grid>
        ) : null}

        {data.button?.text ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 4, laptop: 5 } }}>
            <SalifyLink href={data.button.link || "#"}>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: "#2d2d2d",
                  color: "#fff",
                  fontWeight: 600,
                  px: 3,
                  py: 1.25,
                  borderRadius: 1,
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#1a1a1a", boxShadow: "none" },
                }}
              >
                {data.button.text}
              </Button>
            </SalifyLink>
          </Box>
        ) : null}
      </Container>
    </Box>
  );
};

export default FixedProductCategoryDefaultV2;
